import { Component, Inject, NgZone, DOCUMENT, ChangeDetectorRef } from '@angular/core';
import { MenuController, Platform } from '@ionic/angular';
import { Router, NavigationEnd } from '@angular/router';
import { register } from 'swiper/element/bundle';
import { TranslationService, WebSocketService, AuthService, StorageService, LoaderService, GlobalPrayerService, ShareDataService } from './shared/services';
import { Capacitor } from '@capacitor/core';
import { NavigationBar } from '@capgo/capacitor-navigation-bar';
import { StatusBar } from '@capacitor/status-bar';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { UserType } from './shared/constants';
import { format } from 'date-fns';

import { environment } from 'src/environments/environment';
import { PushNotificationsService } from './shared/services/push-notification/push-notifications.service';
import { LogoutService } from './shared/services/logout-service/logout.service';
import { TranslateService } from '@ngx-translate/core';
import { RTLLanguagesCodes } from './shared/constants/supported-language';
import { SplashScreen } from '@capacitor/splash-screen';
import { NotificationWebsocketService } from './shared/services/notification-websocket-service/notification-websocket.service';
import { TourService } from 'src/app/shared/services/tour/tour.service';
import { Device } from '@capacitor/device';
import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

register();

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false
})
export class AppComponent {

  isMenuDisabled: boolean = false;
  is_admin: boolean = false;

  is_individual : boolean = false;
  is_pastor : boolean = false;
  is_guest : boolean = false;

  hideTopBar: boolean = false;

  menuSegment: 'admin'|'main' = 'main';
  currentUser?: UserType;

  isProductionEnv: boolean = environment.production;

  private isRefreshingToken: boolean = false;
  private meTrigger$ = new Subject<void>();

  constructor(
    public translationService: TranslationService,
    private webSocketService: WebSocketService,
    private globalPrayerService: GlobalPrayerService,
    private platform: Platform,
    private zone: NgZone,
    public menu: MenuController,
    public authService: AuthService,
    private loaderService: LoaderService,
    private authStorageService: StorageService,
    private shareData: ShareDataService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private logoutService: LogoutService,
    public translate: TranslateService,
    public notificationWebSocketService : NotificationWebsocketService,
    public tourService: TourService,
    private cdr: ChangeDetectorRef
  ) {

    if (this.authStorageService.user) {
      this.currentUser = this.authStorageService.user;
      this.is_admin = !!this.authStorageService.user?.roles_list
        ?.map((role) => role.name.toLowerCase())
        .join(',')
        .includes('super_admin');
    }

    this.setupLanguageListener();

    this.updateHideTopBar();
    this.setupRouterListener();

    this.authService.isReady.subscribe(ready => {
      if (ready) {
        this.setAppLang();
        this.isSuperAdminPage();

        this.checkIfUserIsOnlyIndividual();
      }
    });

    this.meTrigger$.pipe(
      switchMap(() => this.authService.me())
    ).subscribe((response) => {
      if (!response.success) return;

      console.log({ USER_ROLE: response.value.roles_list, TYPE_CODE: response.value.type?.code });

      this.applyUserRoleFlags(response.value);

      console.log({ IS_GUEST: this.is_guest });
      if (response.value.name || response.value.profile_picture_url) {
        this.currentUser = { ...this.currentUser, ...response.value } as UserType;
      }
      this.cdr.detectChanges();
    });

    this.authService.currentUser$.subscribe(user => {
      if (user) {
        this.currentUser = user as UserType;
        this.is_admin = !!user?.roles_list
          ?.map((role: any) => role.name.toLowerCase())
          .join(',')
          .includes('super_admin');
        this.applyUserRoleFlags(user);
        this.cdr.detectChanges();
      }
    });

    this.initializeApp();
    this.initDeepLink();

    App.addListener('resume', () => {
      this.setAppLang();
    });

    console.log({CURRENT_USER : this.authStorageService.user});

  }

  private setupLanguageListener()
  {
    this.translate.onLangChange.subscribe((value) => {
      this.document.querySelector("html")?.setAttribute("lang", value.lang.replace("-", "_"));
      if (RTLLanguagesCodes.includes(value.lang.toLowerCase())) {
        this.document.querySelector("html")?.setAttribute("dir", 'rtl');
      } else {
        this.document.querySelector("html")?.setAttribute("dir", 'ltr');
      }
    });
  }

  private updateHideTopBar() {
    const urlPath = this.router.url.split('?')[0].split('#')[0];
    this.hideTopBar = urlPath === '/auth';
  }

  private setupRouterListener() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const isAuthPage = this.router.url.includes('/auth');
        this.menu.enable(!isAuthPage);
        this.updateHideTopBar();

        if (isAuthPage) {
          return;
        }

        this.scrollActiveMenuIntoView();

        if (this.authStorageService.user) {
          this.currentUser = this.authStorageService.user;
          this.is_admin = !!this.authStorageService.user?.roles_list
            ?.map((role) => role.name.toLowerCase())
            .join(',')
            .includes('super_admin');

            this.checkIfUserIsOnlyIndividual();

          const isGuestUser = this.authStorageService.user?.type?.code === 'guest';
          if (!isGuestUser && this.authStorageService.shouldRefreshToken && !this.isRefreshingToken) {
            this.refreshToken();
          }
        } else {
          this.currentUser = undefined;
          this.is_admin = false;
        }
      }
    });
  }

  async initializeApp() {
    await this.platform.ready();
    this.scrollActiveMenuIntoView();
    this.keyboardListener();

    if (Capacitor.isNativePlatform()) {
      NavigationBar.setNavigationBarColor({color: '#d7d8da'});
      await StatusBar.setOverlaysWebView({overlay: true});
      await StatusBar.setBackgroundColor({color: '#00000000'});
    }

    document.documentElement.style.setProperty('--header-toolbar-android-top', '0px');
    const platform = Capacitor.getPlatform();
    document.documentElement.style.setProperty(
    '--header-android-top',
    platform === 'ios' ? '-50px' : platform === 'web' ? '15px' : '-40px'
    );
    document.documentElement.style.setProperty(
      '--you-header-height',
      platform === 'web' ? '330px' : '280px'
    );
    await SplashScreen.hide({fadeOutDuration: 500});

    this.webSocketService.registerWebSocketListeners();
    this.notificationWebSocketService.webSocketListeners();

    const info = await Device.getId();
    const langCode = await Device.getLanguageCode();

    const existingToken = this.authStorageService.isAuthenticated();

    if (!existingToken) {
      this.authService.registerDevideID({deviceId : info.identifier, lang:langCode.value}).subscribe({
        next: (res) => {
          if (res.success) {
            ['world_prays_registered_user_data', 'world_prays_registered_pmd_data', 'world_prays_registered_org_data'].forEach((key) => localStorage.removeItem(key));
            localStorage.setItem("world_prays_token", res.value.token);
            localStorage.setItem("token_issued_date", res.value.issued_time || new Date().toISOString());
            localStorage.setItem("token_expiration_date", res.value.expiration_time);
            localStorage.setItem("world_prays_user", JSON.stringify(res.value.user));
            this.authStorageService.setIsGuest(true);
          }
          this.authService.isReady.next(true);
        },
        error: () => {
          this.authService.isReady.next(true);
        }
      });
    } else {
      this.authService.isReady.next(true);
    }
  }

  ngAfterViewInit() {

  }

  initDeepLink() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        let slug = '/', url = event.url;
        let scheme = (this.platform.is('android') ? 'ltd' : 'mu') + '.ges.worldprays';
        if (url.startsWith('http')) {
            slug = url.split(/\.com/).pop() as string;
        } else if (url.startsWith(scheme)) {
            let glue = scheme + ':/';
            slug = url.split(glue).pop() as string;
        }
        if (slug) {
          await this.router.navigateByUrl(slug);
        }
      });
    });
  }

  async getMyPrayerHour() {
    this.globalPrayerService.getPrayerTime().subscribe(
      async (res: any) => {
        if (res.success) {
          let targetPrayerTime = typeof res.value == 'string' ? format(new Date(res.value), 'Y/MM/dd HH:mm:ss') : '';
          this.shareData.setPrayerTime(targetPrayerTime);
        }
      }
    )
  }

  setAppLang(){
    const lang = localStorage.getItem('currentLang');
    this.translationService.changeLang(lang? lang : 'en');
  }

  keyboardListener() {
    window.addEventListener('keyboardDidShow', (event: any) => {
      this.document.querySelector('html')?.classList.add('keyboard-open');
    })
    window.addEventListener('keyboardDidHide', () => {
      this.document.querySelector('html')?.classList.remove('keyboard-open');
    })
  }

  async refreshToken() {
    this.isRefreshingToken = true;
    this.authService.refreshToken().subscribe({
      next: async (res: any) => {
        console.log({REFRESH_TOKE : res});
        if (res.error === "Unauthorized.") {
          const isGuestUser = this.authStorageService.user?.type?.code === 'guest';
          const tokenExpirationDate = localStorage.getItem('token_expiration_date');
          const isActuallyExpired = tokenExpirationDate ? new Date(tokenExpirationDate).getTime() <= Date.now() : true;
          if (isActuallyExpired && !isGuestUser) {
            await this.loaderService.dismiss();
            ['world_prays_token', 'world_prays_user', 'token_issued_date', 'token_expiration_date'].forEach(
              (key) => {
                localStorage.removeItem(key);
              }
            )
          }
        }
        if (res.success) {
          localStorage.setItem("world_prays_token", res.value.token)
          localStorage.setItem("token_issued_date", res.value.issued_time || new Date().toISOString())
          localStorage.setItem("token_expiration_date", res.value.expiration_time);
        }
        this.isRefreshingToken = false;
      },
      error: async () => {
        this.isRefreshingToken = false;
      }
    })
  }

  async logout() {
    await this.logoutService.logoutEvent.emit();
    await this.loaderService.show('closing_session');
    this.authService.logout().subscribe(
      async (res: any) => {
        if (res.success || res.error === "Unauthorized.") {
          await PushNotificationsService.unregisterNotifications();
          this.authService.clearLocalStorage();

          const info = await Device.getId();
          const langCode = await Device.getLanguageCode();
          console.log({LOGOUT_DEVICE_ID : info.identifier});
          console.log({LOGOUT_DEVICE_LANG_CODE : await Device.getLanguageCode()});
          console.log({LOGOUT_DEVICE_LANG_TAG : await Device.getLanguageTag()});

          this.authService.registerDevideID({deviceId : info.identifier, lang:langCode.value, action : 'logout'}).subscribe(async resGuest => {
            await this.loaderService.dismiss();
            if (resGuest.success) {
              localStorage.setItem("world_prays_token", resGuest.value.token)
              localStorage.setItem("token_issued_date", resGuest.value.issued_time || new Date().toISOString())
              localStorage.setItem("token_expiration_date", resGuest.value.expiration_time);
              localStorage.setItem("world_prays_user", JSON.stringify(resGuest.value.user));
              this.authStorageService.setIsGuest(true);
              this.is_guest = true;

              this.loaderService.dismiss();
              const targetUrl = '/tabs/prayer-for-all';
              const wasAdmin = this.isSuperAdminPage();
              this.zone.run(() => {
                if (wasAdmin) {
                  this.router.navigateByUrl(targetUrl, { replaceUrl: true }).then(() => {
                    this.is_admin = false;
                  });
                } else if (this.router.url === targetUrl) {
                  this.is_admin = false;
                  this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                    this.router.navigateByUrl(targetUrl, { replaceUrl: true });
                  });
                } else {
                  this.is_admin = false;
                  this.router.navigateByUrl(targetUrl, { replaceUrl: true });
                }
              });
            }
          });
        }
      }
    )

  }

  isSuperAdminPage(): boolean {
    return this.router.url.startsWith('/super-admin');
  }

  scrollActiveMenuIntoView(): void {
    setTimeout(() => {
      const activeItem = this.document.querySelector('ion-menu ion-item.selected') as HTMLElement | null;
      if (activeItem) {
        activeItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 300);
  }

  checkIfUserIsOnlyIndividual(): void {
    this.meTrigger$.next();
  }

  private applyUserRoleFlags(user: any): void {
    const userRoles = user?.roles_list || [];
    const typeCode = user?.type?.code;

    const hasIndividual = userRoles.some((r: any) => r.name === 'member_individual');
    const hasPastor = userRoles.some((r: any) => r.name === 'pastor_leader');

    if (userRoles.length === 1 && hasIndividual) {
      this.is_individual = true;
      this.is_pastor = false;
      this.is_guest = false;
    } else if (hasPastor) {
      this.is_individual = false;
      this.is_pastor = true;
      this.is_guest = false;
    } else {
      this.is_individual = false;
      this.is_pastor = false;
      this.is_guest = typeCode === 'guest';
    }
  }
}